import { gql, useMutation, useQuery } from '@apollo/client'
import { RouteComponentProps } from '@reach/router'
import * as chrono from 'chrono-node'
import cx from 'classnames'
import { format, parseISO } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'
import { setHours, setMilliseconds, setMinutes, setSeconds } from 'date-fns/fp'
import { Field, Form, Formik } from 'formik'
import { Link, navigate } from 'gatsby'
import { flow, range } from 'lodash/fp'
import * as yup from 'yup'
import { chronoDate } from '../../schema/date'
import DateInput from '../DateInput'
import TextInput from '../TextInput'
import { DashboardTitle } from './DashboardTitleContext'
import { useToast } from './Toast'
import { CreateRapidSession, CreateRapidSessionVariables } from './__generated__/CreateRapidSession'

const casualGbChrono = new chrono.Chrono(chrono.en.createCasualConfiguration(true))

const allUsersQuery = gql`
  query resultsUsers {
    usersByGroup(groupName: "add_results") {
      id
      email
    }
  }
`

const createRapidSessionMutation = gql`
  mutation CreateRapidSession($input: NewRapidSessionInput!) {
    createRapidSession(input: $input) {
      success
      message
      rapidSession {
        id
      }
    }
  }
`

const validationSchema = yup.object().shape({
  startDate: chronoDate(casualGbChrono).label('Start date').typeError('Not a valid date').required(),
})

const TimeChooser = ({ name, label }) => (
  <div className="field">
    <label className="label is-size-7 has-text-weight-light">{label}</label>
    <div className="select">
      <Field as="select" name={`${name}Hours`}>
        {range(0, 24).map((hour) => (
          <option key={hour} value={hour}>
            {hour.toString().padStart(2, '0')}
          </option>
        ))}
      </Field>
    </div>
    <span className="is-size-4">&nbsp;:&nbsp;</span>
    <div className="select">
      <Field as="select" name={`${name}Minutes`}>
        {range(0, 60).map((minute) => (
          <option key={minute} value={minute}>
            {minute.toString().padStart(2, '0')}
          </option>
        ))}
      </Field>
    </div>
  </div>
)

const NewSessionForm = ({ thirdPartyId, users }) => {
  const [createRapidSession] = useMutation<CreateRapidSession, CreateRapidSessionVariables>(createRapidSessionMutation)
  const { addToast } = useToast()

  const initialValues = {
    isAllDay: true,
    startDate: 'Tomorrow',
    startHours: 0,
    startMinutes: 0,
    endDate: 'Tomorrow',
    endHours: 23,
    endMinutes: 59,
    ref: '',
    notes: '',
    selectedUsers: users.map((user) => ({
      selected: false,
      id: user.id,
      email: user.email,
    })),
  }

  return (
    <Formik
      onSubmit={async (values) => {
        try {
          const startDate = flow([
            (s: string) => casualGbChrono.parseDate(s),
            setHours(values.isAllDay ? 0 : values.startHours),
            setMinutes(values.isAllDay ? 0 : values.startMinutes),
            setSeconds(0),
            setMilliseconds(0),
            (d: Date) => zonedTimeToUtc(d, 'Europe/London'),
            (d: Date) => d.toISOString(),
          ])(values.startDate)

          const endDate = flow([
            (s: string) => casualGbChrono.parseDate(s),
            setHours(values.isAllDay ? 23 : values.endHours),
            setMinutes(values.isAllDay ? 59 : values.endMinutes),
            setSeconds(0),
            setMilliseconds(0),
            (d: Date) => zonedTimeToUtc(d, 'Europe/London'),
            (d: Date) => d.toISOString(),
          ])(values.isAllDay ? values.startDate : values.endDate)

          const { data } = await createRapidSession({
            variables: {
              input: {
                startDate,
                endDate,
                thirdPartyId,
                reference: values.ref,
                notes: values.notes,
                resultsUserEmails: values.selectedUsers.filter((u) => u.selected).map((u) => u.email),
              },
            },
          })

          const response = data?.createRapidSession

          if (response.success !== true) {
            addToast({
              messageType: 'error',
              title: 'Error',
              content: response.message,
            })
            return
          }

          const thirdPartyUrl = `/dashboard/third-party/${thirdPartyId}`
          const sessionUrl = `${thirdPartyUrl}/sessions/${response.rapidSession.id}`

          addToast({
            messageType: 'success',
            title: 'Session created',
            timeout: 5000,
            content: (
              <>
                Created session for third party <Link to={thirdPartyUrl}>{thirdPartyId}</Link> on{' '}
                {format(parseISO(startDate), 'dd/MM/yy')}. <Link to={sessionUrl}>Go to session</Link>.
              </>
            ),
          })

          navigate(thirdPartyUrl)
        } catch (err) {
          addToast({
            messageType: 'error',
            title: 'Error',
            content: `Failed to create session: ${err.message}`,
          })
        }
      }}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {({ values, isSubmitting }) => (
        <Form>
          <div className="columns">
            <div className="column">
              <div className="title">Session Details</div>

              <label className="checkbox mb-3">
                <Field type="checkbox" name="isAllDay" />
                &nbsp;All Day session
              </label>

              {values.isAllDay ? (
                <DateInput name="startDate" label="Date" chronoInstance={casualGbChrono} />
              ) : (
                <>
                  <DateInput name="startDate" label="Start date" chronoInstance={casualGbChrono} />

                  <TimeChooser name="start" label="Start time" />

                  <DateInput name="endDate" label="End date" chronoInstance={casualGbChrono} />

                  <TimeChooser name="end" label="End time" />
                </>
              )}

              <TextInput name="ref" label="Reference" type="text" />

              <label htmlFor="ref" className="label is-size-7 has-text-weight-light">
                Notes
              </label>
              <Field className="textarea" name="notes" label="Notes" component="textarea" />
            </div>

            <div className="column">
              <div className="title">Staff</div>
              <p>Select which users will be able to enter results for this session.</p>
              <p>
                <strong>Note: </strong>Selph admin staff already have access.
              </p>
              <ul>
                {values.selectedUsers.map((user, i) => (
                  <li key={user.id} className="my-2">
                    <label className="checkbox">
                      <Field name={`selectedUsers[${i}].selected`} type="checkbox" /> {user.email}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="buttons">
            <button
              type="submit"
              className={cx('button is-primary', {
                'is-loading': isSubmitting,
              })}
            >
              Create Session
            </button>
          </div>
        </Form>
      )}
    </Formik>
  )
}

const NewSession = ({ thirdPartyId }: RouteComponentProps<{ thirdPartyId: string }>) => {
  const { loading, data } = useQuery(allUsersQuery)

  return (
    <div className="container">
      <DashboardTitle title="New Session" />

      {loading && <p>Loading...</p>}
      {data?.usersByGroup && <NewSessionForm thirdPartyId={thirdPartyId} users={data.usersByGroup} />}
    </div>
  )
}

export default NewSession
